import { gql } from "lib/apolloClient";
import {
  attachmentFragment,
  studentFragment,
  userFragment,
  attachmentGroupsFragment,
  resolvedFieldValueFragment,
  toolResponseFragment,
  plannerTemplateFragment,
  studentsAlertFragment,
  subjectFragment,
  fieldScoreFragment,
  subjectGroupFragment,
  unitPlanFragment as postUnitPlanFragment,
  criteriaSetFragment,
  criteriaValueFragment,
  similarityFragment,
} from "modules/CommonFragments";

import { folderFragments } from "Journal/modules/JournalFragments";

export const focusAreaFragment = {
  focusArea: gql`
    fragment focusAreaItem on FocusArea {
      id
      label
    }
  `,
  focusAreaSet: gql`
    fragment focusAreaSetItem on PlannerFocusAreaSet {
      id
      label
    }
  `,
};

export const pypElementFragment = {
  theme: gql`
    fragment themeItem on PlannerTheme {
      id
      label
      descriptor
      metadata
      associatedChildren {
        id
        label
      }
      focusAreas {
        ...focusAreaItem
      }
      icon
    }
    ${focusAreaFragment.focusArea}
  `,
  lp: gql`
    fragment lpItem on PlannerLearnerProfile {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  concept: gql`
    fragment conceptItem on PlannerConcept {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  atl: gql`
    fragment atlItem on PlannerATL {
      id
      label
      children
      parent
      levelId
      levels {
        id
        value
      }
      depth
      isLeaf
      displaySequence
      atlCategory {
        id
      }
      subText
      grades {
        id
        name
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  benchmark: gql`
    fragment benchmarkItem on PlannerBenchmark {
      id
      label
      subText
      children
      parent
      benchmarkQuestionCount {
        count
        color
        url
        yearSplit
      }
      levels {
        id
        value
        count
      }
      subject
      gradingType
      levelId
      displaySequence
      depth
      type
      code
      grades {
        id
        name
      }
      isLeaf
      genericTags: tags {
        id
        label
        icon {
          id
          url
        }
      }
      resolvedSubject {
        id
        isArchived
      }
      standardsWrapper {
        standardSet {
          id
          title
        }
        properties {
          isGradedStandard
        }
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  benchmarkV2: gql`
    fragment benchmarkItem on PlannerBenchmark {
      id
      label
      subText
      children
      parentId: parent
      benchmarkQuestionCount {
        count
        color
        url
        yearSplit
      }
      subject
      depth
      code
      grades {
        id
        name
      }
      genericTags: tags {
        id
        label
      }
      resolvedSubject {
        id
        isArchived
      }
    }
  `,
  benchmarkStandard: gql`
    fragment benchmarkStandardItem on StandardBenchmark {
      id
      label
      children
      parent
      parentId
      subject
      levelId
      displaySequence
      depth
      type
      code
      grades {
        id
        name
      }
      isLeaf
    }
  `,
  relatedConcept: gql`
    fragment relatedConceptItem on PlannerRelatedConcept {
      id
      label
      elementId
      elementType
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  action: gql`
    fragment actionItem on Action {
      id
      label
      subText
      children
      parent
      levels {
        id
        value
      }
      levelId
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  plannerElementNode: gql`
    fragment plannerElementNode on PlannerElementNode {
      id
      label
      depth
      children
      type
    }
  `,
};

export const postPypElementRatingFragment = {
  studentElementRating: gql`
    fragment ratingItem on IBPYPElementRating {
      id
      value
      ibPYPElementId
      ibPYPElementType
      subjectId
      learningCourse {
        id
      }
      academicCriteriaSet {
        ...criteriaSetItem
      }
      academicCriteriaValue {
        ...criteriaValueItem
      }
      creatorType
    }

    ${criteriaSetFragment.criteriaSet}
    ${criteriaValueFragment.criteriaValue}
  `,
};

export const commonResolvedFieldValueFragment = gql`
  fragment commonResolvedFieldValueItem on ResolvedFieldValue {
    id
    type
    ... on ResolvedFieldBenchmarkSet {
      id
      benchmarks {
        ...benchmarkItem
      }
    }
    ... on ResolvedFieldConceptSet {
      id
      concepts {
        ...conceptItem
      }
    }
    ... on ResolvedFieldActionSet {
      id
      actions {
        ...actionItem
      }
    }
    ... on ResolvedFieldLPSet {
      id
      learnerProfiles {
        ...lpItem
      }
    }
    ... on ResolvedFieldThemeSet {
      id
      themes {
        ...themeItem
      }
    }
    ... on ResolvedFieldSubjectSet {
      id
      subjects {
        ...subjectItem
        subjectGroup {
          id
          name
          constants
          globalSubjectGroup {
            id
            name
            constants
          }
        }
      }
    }
    ... on ResolvedFieldATLSet {
      id
      atls {
        ...atlItem
      }
    }
    ... on ResolvedFieldRelatedConceptSet {
      id
      relatedConcepts {
        ...relatedConceptItem
      }
    }
    ... on ResolvedFieldVoiceInstruction {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldVideoAttachment {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldStudentTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldLtiTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldTags {
      id
      tags {
        id
        label
      }
    }
    ... on ResolvedFieldGenericTags {
      id
      genericTags {
        id
        label
        __typename
      }
      __typename
    }
    ... on ResolvedFieldPlannerElementSet {
      id
      type
      nodes {
        ...plannerElementNodeItem
        gradingType
        standardsWrapper {
          standardSet {
            id
            title
          }
          properties {
            isGradedStandard
          }
        }
      }
    }
    ... on ResolvedFieldSubjectGroup {
      id
      type
      subjectGroups {
        ...subjectGroupWithGlobalSubjectGroupItem
      }
    }
    ... on ResolvedFieldScore {
      ...fieldScoreItem
    }
    ... on ResolvedFieldObjectiveLevel {
      id
      nodes {
        id
        label
        associatedParents {
          id
        }
      }
    }
    ... on ResolvedFieldAttachments {
      id
      type
      attachments {
        ...attachmentItem
      }
    }
  }
`;

export const postPlannerElementNodeFragment = {
  basicPlannerElementNodeDetail: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      label
      subText
    }
  `,
  plannerElementNode: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      displaySequence
      label
      subText
      code
      type
      subText
      depth
      children
      parent
      subType
      isLeaf
      code
      metadata
      updatedAt
      gradingType
      plannerElementNodeWrapper {
        properties {
          isGradedStandard
        }
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
      standardsWrapper {
        standardSet {
          id
          title
        }
        properties {
          isGradedStandard
        }
      }
      associatedParents {
        id
        ... on Grade {
          id
          name
        }
        ... on Subject {
          id
          name
          isArchived
        }
        ... on SubjectGroup {
          ...subjectGroupBasicDetailsItem
        }
        ... on PlannerElementNode {
          id
          label
          type
          metadata
        }
      }
      tags: genericTags {
        id
        label
        icon {
          id
          url
        }
      }
    }
    ${subjectGroupFragment.subjectGroupBasicDetails}
  `,
  plannerElementNodeWithChild: gql`
    fragment plannerElementNodeWithChildItem on PlannerElementNode {
      id
      label
      type
      associatedParents {
        id
        ... on Course {
          id
          title
        }
      }
      associatedChildPlannerElementNodes {
        id
        label
        type
        fields {
          id
          uid
          value
        }
      }
    }
  `,
  plannerElementDepthLabels: gql`
    fragment plannerElementDepthLabelItem on PlannerElementDepthLabel {
      id
      depth
      label
      totalNodes
    }
  `,
};

export const postResolvedFieldValueFragment = {
  resolvedMinimalTree: gql`
    fragment resolvedMinimalTreeItem on ResolvedFieldValue {
      ... on ResolvedFieldUnitPlan {
        id
        type
        unitPlan {
          ...unitPlanBasicDetailsItem
        }
      }
      ...commonResolvedFieldValueItem
    }
    ${subjectGroupFragment.subjectGroupBasicDetails}
    ${postPlannerElementNodeFragment.plannerElementNode}
    ${pypElementFragment.benchmark}
    ${pypElementFragment.concept}
    ${pypElementFragment.action}
    ${pypElementFragment.lp}
    ${pypElementFragment.theme}
    ${pypElementFragment.atl}
    ${pypElementFragment.relatedConcept}
    ${subjectFragment.subjectBenchmarkDetails}
    ${attachmentFragment.attachment}
    ${attachmentGroupsFragment.attachmentGroup}
    ${fieldScoreFragment.fieldScore}
    ${postUnitPlanFragment.unitPlanBasicDetails}
    ${commonResolvedFieldValueFragment}
  `,
};

const resolvedFieldValueItemFragment = gql`
  fragment resolvedFieldValueItem on ResolvedFieldValue {
    id
    type
    ... on ResolvedFieldBenchmarkSet {
      id
      benchmarks {
        ...benchmarkItem
      }
    }
    ... on ResolvedFieldConceptSet {
      id
      concepts {
        ...conceptItem
      }
    }
    ... on ResolvedFieldActionSet {
      id
      actions {
        ...actionItem
      }
    }
    ... on ResolvedFieldLPSet {
      id
      learnerProfiles {
        ...lpItem
      }
    }
    ... on ResolvedFieldThemeSet {
      id
      themes {
        ...themeItem
      }
    }
    ... on ResolvedFieldSubjectSet {
      id
      subjects {
        ...subjectItem
        subjectGroup {
          id
          name
          constants
          globalSubjectGroup {
            id
            name
            constants
          }
        }
      }
    }
    ... on ResolvedFieldATLSet {
      id
      atls {
        ...atlItem
      }
    }
    ... on ResolvedFieldRelatedConceptSet {
      id
      relatedConcepts {
        ...relatedConceptItem
      }
    }
    ... on ResolvedFieldVideoAttachment {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldStudentTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldTags {
      id
      tags {
        id
        label
      }
    }
    ... on ResolvedFieldGenericTags {
      id
      genericTags {
        id
        label
        __typename
      }
      __typename
    }
    ... on ResolvedFieldPlannerElementSet {
      id
      type
      nodes {
        ...plannerElementNodeItem
      }
    }
    ... on ResolvedFieldSubjectGroup {
      id
      type
      subjectGroups {
        ...subjectGroupWithGlobalSubjectGroupItem
      }
    }
    ... on ResolvedFieldScore {
      ...fieldScoreItem
    }
    ... on ResolvedFieldObjectiveLevel {
      id
      nodes {
        id
        label
        associatedParents {
          id
        }
      }
    }
    ... on ResolvedFieldAttachments {
      id
      type
      attachments {
        ...attachmentItem
      }
    }
  }
  ${postPlannerElementNodeFragment.plannerElementNode}
  ${pypElementFragment.benchmark}
  ${pypElementFragment.concept}
  ${pypElementFragment.action}
  ${pypElementFragment.lp}
  ${pypElementFragment.theme}
  ${pypElementFragment.atl}
  ${pypElementFragment.relatedConcept}
  ${fieldScoreFragment.fieldScore}
  ${attachmentFragment.attachment}
  ${attachmentGroupsFragment.attachmentGroup}
  ${subjectGroupFragment.subjectGroupWithGlobalSubjectGroup}
  ${subjectFragment.subjectBenchmarkDetails}
`;

const resolvedFieldValueFragmentV2 = {
  resolvedMinimalTree: gql`
    fragment unitPlanResolvedMinimalTreeItem on ResolvedFieldValue {
      ...resolvedFieldValueItem
    }
    ${resolvedFieldValueItemFragment}
  `,
};

const plannerFieldFragment = {
  unitPlanAllFields: gql`
    fragment unitPlanAllFields on PlannerField {
      id
      uid
      value
      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
    }
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
};

export const unitPlanFragment = {
  unitPlan: gql`
    fragment unitPlanItem on UnitPlan {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      unitType {
        id
        value
      }
      subjects {
        id
        value
      }

      templateId
    }
  `,
  unitPlanDetails: gql`
    fragment unitPlanItem on UnitPlan {
      id
      title {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ...resolvedMinimalTreeItem
        }
      }
      centralIdea {
        id
        value
      }
      unitType {
        id
        value
      }
      subjects {
        id
        value
      }
      theme {
        id
        value
      }
      resourceLibrary {
        leCount
        assessmentCount
      }

      grades
      fields(
        uids: [
          "lp"
          "centralIdea"
          "concepts"
          "atls"
          "benchmarks"
          "lois"
          "learningStandardMYP"
          "learningStandardUBD"
          "subjectStandardDP"
        ]
      ) {
        id
        uid
        value
        resolvedMinimalTree {
          ...resolvedMinimalTreeItem
        }
      }
      templateId
    }
    ${resolvedFieldValueFragment.resolvedMinimalTree}
  `,
  unitPlanDetailsWithoutFields: gql`
    fragment unitPlanItem on UnitPlan {
      id
      title {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ...resolvedFieldVideoAttachmentItem
        }
      }
      centralIdea {
        id
        value
      }
      unitType {
        id
        value
      }
      subjects {
        id
        value
      }
      theme {
        id
        value
      }
      resourceLibrary {
        leCount
        assessmentCount
      }
      grades
      templateId
      fields(uids: $uids) {
        id
        uid
        value
        resolvedMinimalTree {
          ...resolvedMinimalTreeItem
        }
      }
    }
    ${resolvedFieldValueFragment.resolvedFieldVideoAttachment}
    ${postResolvedFieldValueFragment.resolvedMinimalTree}
  `,
};

export const studentCourseDetailFragment = {
  basicStudentDetails: gql`
    fragment studentItem on Student {
      id
      firstName
      middleName
      lastName
      pronouns
      preferredName
      prefix
      suffix
      profileImage
      email
      type
      isDevUser
      isArchived
      isArchivedV2(filters: $userArchivedFilters)
      sourceId
      type
    }
  `,
};

export const messageCommentOfPostFragment = {
  message: gql`
    fragment messageItem on Message {
      id
      label
      itemType
      type
      isDeleted
      isCommentRead
      isRead
      item {
        id
        ... on AttachmentSet {
          id
          attachments {
            ...attachmentItem
          }
        }
      }
      createdAt
      createdBy {
        id
        firstName
        middleName
        lastName
        middleName
        ... on Student {
          pronouns
          preferredName
          suffix
          alertTypes {
            ...alertTypeFields
          }
        }
        ... on Staff {
          preferredName
          prefix
          suffix
          pronouns
        }

        ... on FamilyMember {
          preferredName
          prefix
          suffix
          pronouns
        }
        type
        email
        profileImage
      }
    }
    ${studentsAlertFragment}
    ${attachmentFragment.attachment}
  `,
};

const taggedPracticesFragment = {
  taggedPracticesDetails: gql`
    fragment taggedPracticesDetails on SnPTaggedPractice {
      evaluationCycle {
        id
      }
      taggedPractice {
        taggedBy {
          id
        }
        evidenceStatus
        practice {
          id
          code
          type
          label: localeLabelKey
          localizedLabel: label
        }
      }
    }
  `,
};

export const likefragment = {
  like: gql`
    fragment likeItem on Like {
      id
      user {
        id
        firstName
        lastName
        middleName
        type
        ... on Student {
          pronouns
          preferredName
          suffix
          prefix
        }
        ... on Staff {
          preferredName
          prefix
          suffix
          pronouns
        }

        ... on FamilyMember {
          preferredName
          prefix
          suffix
          pronouns
        }
      }
    }
  `,
};

export const postSeenFragment = {
  postSeen: gql`
    fragment postSeenItem on Seen {
      id
      user {
        id
      }
    }
  `,
};

export const activityLikeCommentFragment = {
  likesAndComments: gql`
    fragment activityLikesAndComments on StudentActivity {
      id
      comments {
        totalCount
        unreadCountV2
        edges: messageEdges {
          node {
            ...messageItem
          }
        }
      }
      likes {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
    }
    ${messageCommentOfPostFragment.message}
    ${likefragment.like}
  `,
};

export const PlannerFieldFragment = {
  PlannerFieldDetail: gql`
    fragment PlannerFieldItem on PlannerField {
      id
      uid
      value
      resolvedMinimalTree {
        ...resolvedMinimalTreeItem
      }
    }
    ${postResolvedFieldValueFragment.resolvedMinimalTree}
  `,
};

export const resourceFragments = {
  assessmentItem: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      assessmentType {
        id
        uid
        value
      }
      taskType {
        id
        type
        label
      }
    }
  `,
  leItem: gql`
    fragment leItem on LearningEngagement {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
    }
  `,
  assessmentDetails: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      allFields {
        id
        uid
        value
      }
      fields(
        uids: ["lp", "centralIdea", "concepts", "atls", "benchmarks", "lois"]
      ) {
        id
        uid
        value
      }
      assessmentType {
        id
        uid
        value
      }
      unitPlan {
        id
      }
    }
  `,
  leDetails: gql`
    fragment leItem on LearningEngagement {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      fields(
        uids: ["lp", "centralIdea", "concepts", "atls", "benchmarks", "lois"]
      ) {
        id
        uid
        value
      }
      allFields {
        id
        uid
        value
      }
      unitPlan {
        id
      }
    }
  `,
  assessmentDetailsVariable: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      allFields {
        id
        uid
        value
      }
      assessmentType {
        id
        uid
        value
      }
    }
  `,
  leDetailsVariable: gql`
    fragment leItem on LearningEngagement {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      allFields {
        id
        uid
        value
      }
    }
  `,
  assessmentBasicDetails: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      assessmentType {
        id
        uid
        value
      }
      unitPlan {
        id
        title {
          id
          value
        }
      }
      fields(uids: $uids) {
        id
        uid
        value
        resolvedMinimalTree {
          ...resolvedMinimalTreeItem
        }
      }
    }
    ${postResolvedFieldValueFragment.resolvedMinimalTree}
  `,
  assessmentBasicDetailsV2: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      assessmentType {
        id
        uid
        value
      }
      fields(
        uids: [
          "lp"
          "centralIdea"
          "concepts"
          "atls"
          "benchmarks"
          "lois"
          "learningStandardMYP"
          "learningStandardUBD"
          "subjectStandardDP"
        ]
      ) {
        id
        uid
        value
        resolvedMinimalTree {
          ... on ResolvedFieldBenchmarkSet {
            id
            benchmarks {
              id
              standardsWrapper {
                standardSet {
                  id
                  title
                }
                properties {
                  isGradedStandard
                }
              }
            }
          }
          ... on ResolvedFieldPlannerElementSet {
            id
            type
            nodes {
              id
              gradingType
              standardsWrapper {
                standardSet {
                  id
                  title
                }
                properties {
                  isGradedStandard
                }
              }
            }
          }
        }
      }
    }
  `,
  assessmentPYPElements: gql`
    fragment assessmentItem on Assessment {
      id
      unitPlan {
        id
        subjects {
          id
          resolvedMinimalTree {
            ... on ResolvedFieldSubjectSet {
              id
              type
              subjects {
                id
                name
                isDeleted
                isArchived
              }
            }
          }
        }
      }
      fields(uids: $uids) {
        ...PlannerFieldItem
      }
    }
    ${PlannerFieldFragment.PlannerFieldDetail}
  `,
};

const postRatingsFragment = {
  postRatingsItem: gql`
    fragment postRatingsItem on IBPYPElementRating {
      id
      value
      ibPYPElementId
      ibPYPElementType
      academicCriteriaSet {
        id
        label
      }
      academicCriteriaValue {
        id
        label
        abbreviation
        description
      }
      academicTerm {
        id
      }
      student {
        id
      }
    }
  `,
};
export const journalPostFragment = {
  journalPost: gql`
    fragment postItem on Post {
      id
      title
      attachments {
        ...attachmentItem
        displaySequence
      }
      createdBy {
        ...userItem
      }
      createdAt
      publishedAt
      comments {
        totalCount
        unreadCount
      }
      likes {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
      seen {
        totalCount
        edges {
          node {
            ...postSeenItem
          }
        }
      }
      state
      aiConversation {
        edges {
          node {
            id
          }
        }
      }
      course {
        id
        title
      }
      academicTerm {
        id
        label
        startDate
        endDate
      }
      postTaggedEntity {
        id
        entityType
        entity {
          ... on UnitPlan {
            allFields {
              ...unitPlanAllFields
            }
            ...unitPlanItem
          }
          ... on Assessment {
            ...assessmentItem
            unitPlan {
              ...unitPlanItem
            }
          }
        }
      }
      students {
        totalCount
        edges {
          node {
            ...studentItem
          }
        }
      }
      elementPYP {
        id
        fieldUID
        value
        parentType
        resolvedMinimalTree {
          ...resolvedMinimalTreeItem
        }
      }
      ibPYPElementRatings {
        ...postRatingsItem
      }
      folders {
        ...folderItem
      }
      taggedPractices {
        ...taggedPracticesDetails
      }
      isPrivate
      isHiddenFromStudents
      rejectComment
      approveOrRejectBy {
        ...userItem
      }
      items {
        item {
          ... on StudentAssignment {
            id
            isEvaluated
            isSharedWithStudent
            student {
              ...studentItem
            }
            submission {
              id
              response
              attachmentGroups {
                id
                attachments {
                  ...attachmentItem
                }
              }
            }
            toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
              id
              toolId
              toolType
              toolResponses {
                ...toolResponseItem
              }
            }
            ibPYPElementRatings {
              ...ratingItem
            }
            elementRemarks(filters: { creatorTypes: [STAFF] }) {
              id
              message
              ibPYPElementId
              ibPYPElementType
            }
            assignment {
              id
              isStudentEvaluationEnabled
              isTeacherEvaluationEnabled
              content {
                ... on Assessment {
                  id
                  title {
                    id
                    value
                  }
                  assessmentType {
                    id
                    value
                  }
                }
              }
            }
          }
          ... on StudentActivity {
            id
            description
            metadata
            activityType
            startTime
            endTime
            attachmentGroups {
              id
              attachments {
                ...attachmentItem
              }
              metadata
              displaySequence
            }
            activityStudent: student {
              ...studentItem
            }
            ibPYPElementRatings {
              id
              academicCriteriaSet {
                id
                label
                displaySequence
              }
              academicCriteriaValue {
                id
                label
                metadata
              }
              metadata
            }
            ...activityLikesAndComments
          }
        }
        itemType
      }
    }
    ${studentFragment.basicStudentDetails}
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${likefragment.like}
    ${postSeenFragment.postSeen}
    ${taggedPracticesFragment.taggedPracticesDetails}
    ${folderFragments.folderItem}
    ${toolResponseFragment.toolResponse}
    ${postPypElementRatingFragment.studentElementRating}
    ${resolvedFieldValueFragment.resolvedMinimalTree}
    ${activityLikeCommentFragment.likesAndComments}
    ${postRatingsFragment.postRatingsItem}
    ${unitPlanFragment.unitPlanDetails}
    ${plannerFieldFragment.unitPlanAllFields}
    ${resourceFragments.assessmentItem}
  `,
  journalPostFeed: gql`
    fragment postItem on Post {
      id
      title
      titleTranslationItem(filters: $translationFilter) {
        label
        showTranslate
        sourceLanguage
        translatedLabel
      }
      attachments {
        ...attachmentItem
        displaySequence
      }
      createdBy {
        ...userItem
      }
      createdAt
      publishedAt
      comments {
        totalCount
        unreadCount
        edges: messageEdges(first: 1) {
          node {
            ...messageItem
          }
        }
      }
      likes {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
      seen {
        totalCount
        edges {
          node {
            ...postSeenItem
          }
        }
      }
      state
      aiConversation {
        edges {
          node {
            id
          }
        }
      }
      course {
        id
        title
      }
      academicTerm {
        id
        label
        startDate
        endDate
      }
      postTaggedEntity {
        id
        entityType
        entity {
          ... on UnitPlan {
            ...unitPlanItem
          }
          ... on Assessment {
            ...assessmentItem
            unitPlan {
              ...unitPlanItem
            }
          }
        }
      }
      students {
        totalCount
        edges {
          node {
            ...studentItem
            alertTypes {
              ...alertTypeFields
            }
          }
        }
      }
      resourceType
      resourceItem {
        id
        title {
          id
          value
        }
        templateId
        ... on Assessment {
          assessmentType {
            id
            value
          }
          taskType {
            id
            type
            label
          }
        }
      }
      elementPYP {
        id
        fieldUID
        parentType
      }
      ibPYPElementRatings {
        id
      }
      folders {
        ...folderItem
      }
      taggedPractices {
        ...taggedPracticesDetails
      }
      isPrivate
      isHiddenFromStudents
      rejectComment
      isStudentIndividuallyRated
      approveOrRejectBy {
        ...userItem
      }
      items {
        item {
          ... on StudentAssignment {
            id
            isEvaluated
            isSharedWithStudent
            student {
              ...studentItem
            }
            submission {
              id
              response
              attachmentGroups {
                id
                attachments {
                  ...attachmentItem
                }
              }
            }
            toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
              id
              toolId
              toolType
              toolResponses {
                ...toolResponseItem
              }
            }
            ibPYPElementRatings {
              ...ratingItem
            }
            elementRemarks(filters: { creatorTypes: [STAFF] }) {
              id
              message
              ibPYPElementId
              ibPYPElementType
            }
            assignment {
              id
              isStudentEvaluationEnabled
              isTeacherEvaluationEnabled
              content {
                ... on Assessment {
                  id
                  title {
                    id
                    value
                  }
                  assessmentTools {
                    edges {
                      node {
                        id
                        assessmentToolItem {
                          id
                          ... on MypObjectiveRubric {
                            id
                            label
                          }
                          ... on ScoreAssessmentTool {
                            id
                            scoreToolData {
                              id
                              maxScore
                            }
                          }
                        }
                        assessmentToolType
                        isTeacherEvaluationEnable
                        isStudentEvaluationEnable
                      }
                    }
                  }
                  assessmentToolType
                  fields(uids: $uids) {
                    id
                    uid
                    value
                    resolvedMinimalTree {
                      ...resolvedMinimalTreeItem
                    }
                  }
                  allFields @include(if: $isDP) {
                    # TODO: Required for DP response post having learning goals. have check why fields is not fetching goals.
                    id
                    uid
                    value
                    resolvedMinimalTree {
                      ...resolvedMinimalTreeItem
                    }
                  }
                  isLearningGoalsEvaluationEnabled
                }
              }
            }
          }
          ... on StudentActivity {
            id
            description
            activityType
            metadata
            startTime
            endTime
            attachmentGroups {
              id
              attachments {
                ...attachmentItem
              }
              metadata
              displaySequence
            }
            activityStudent: student {
              ...studentItem
            }
            ibPYPElementRatings {
              id
              academicCriteriaSet {
                id
                label
                displaySequence
              }
              academicCriteriaValue {
                id
                label
                metadata
              }
              metadata
            }
            ...activityLikesAndComments
          }
        }
        itemType
      }
    }
    ${studentsAlertFragment}
    ${studentFragment.basicStudentDetails}
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${likefragment.like}
    ${postSeenFragment.postSeen}
    ${taggedPracticesFragment.taggedPracticesDetails}
    ${folderFragments.folderItem}
    ${toolResponseFragment.toolResponse}
    ${postResolvedFieldValueFragment.resolvedMinimalTree}
    ${postPypElementRatingFragment.studentElementRating}
    ${messageCommentOfPostFragment.message}
    ${activityLikeCommentFragment.likesAndComments}
    ${unitPlanFragment.unitPlan}
    ${resourceFragments.assessmentItem}
  `,
  journalPostFeedStudentProfile: gql`
    fragment postItem on Post {
      id
      title
      titleTranslationItem(filters: $translationFilter) {
        label
        showTranslate
        sourceLanguage
        translatedLabel
      }
      attachments {
        ...attachmentItem
        displaySequence
      }
      createdBy {
        ...userItem
      }
      postTaggedEntity {
        id
        entityType
        entity {
          ... on UnitPlan {
            id
          }
          ... on Assessment {
            ...assessmentItem
            unitPlan {
              id
            }
          }
        }
      }
      createdAt
      publishedAt
      comments {
        totalCount
        unreadCount
        edges: messageEdges(first: 1) {
          node {
            ...messageItem
          }
        }
      }
      likes {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
      seen {
        totalCount
        edges {
          node {
            ...postSeenItem
          }
        }
      }
      state
      aiConversation {
        edges {
          node {
            id
          }
        }
      }
      course {
        id
        title
        learningCourse {
          id
          title
        }
        aclControlParameters(filters: $aclControlParameterFilter) {
          id
          uid
          permission
          value
        }
      }
      academicTerm {
        id
        label
        startDate
        endDate
      }
      students {
        totalCount
        edges {
          node {
            ...studentItem
          }
        }
      }
      resourceType
      resourceItem {
        id
        title {
          id
          value
        }
        templateId
        ... on Assessment {
          assessmentType {
            id
            value
          }
          taskType {
            id
            type
            label
          }
        }
      }
      elementPYP {
        id
        fieldUID
        parentType
      }
      ibPYPElementRatings {
        id
      }
      folders {
        ...folderItem
      }
      taggedPractices {
        ...taggedPracticesDetails
      }
      isPrivate
      isHiddenFromStudents
      rejectComment
      isStudentIndividuallyRated
      approveOrRejectBy {
        ...userItem
      }
      items {
        item {
          ... on StudentAssignment {
            id
            isEvaluated
            isSharedWithStudent
            student {
              ...studentItem
            }
            submission {
              id
              response
              attachmentGroups {
                id
                attachments {
                  ...attachmentItem
                }
              }
            }
            toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
              id
              toolId
              toolType
              toolResponses {
                ...toolResponseItem
              }
            }
            ibPYPElementRatings {
              ...ratingItem
            }
            elementRemarks(filters: { creatorTypes: [STAFF] }) {
              id
              message
              ibPYPElementId
              ibPYPElementType
            }
            assignment {
              id
              isStudentEvaluationEnabled
              isTeacherEvaluationEnabled
              content {
                ... on Assessment {
                  id
                  title {
                    id
                    value
                  }
                  assessmentTools {
                    edges {
                      node {
                        id
                        assessmentToolItem {
                          id
                          ... on MypObjectiveRubric {
                            id
                            label
                          }
                          ... on ScoreAssessmentTool {
                            id
                            scoreToolData {
                              id
                              maxScore
                            }
                          }
                        }
                        assessmentToolType
                        isTeacherEvaluationEnable
                        isStudentEvaluationEnable
                      }
                    }
                  }
                  assessmentToolType
                  fields(uids: $uids) {
                    id
                    uid
                    value
                    resolvedMinimalTree {
                      ...resolvedMinimalTreeItem
                    }
                  }
                  allFields @include(if: $isDP) {
                    # TODO: Required for DP response post having learning goals. have check why fields is not fetching goals.
                    id
                    uid
                    value
                    resolvedMinimalTree {
                      ...resolvedMinimalTreeItem
                    }
                  }
                  isLearningGoalsEvaluationEnabled
                }
              }
            }
          }
          ... on StudentActivity {
            id
            description
            activityType
            metadata
            startTime
            endTime
            attachmentGroups {
              id
              attachments {
                ...attachmentItem
              }
              metadata
              displaySequence
            }
            activityStudent: student {
              ...studentItem
            }
            ibPYPElementRatings {
              id
              academicCriteriaSet {
                id
                label
                displaySequence
              }
              academicCriteriaValue {
                id
                label
                metadata
              }
              metadata
            }
            ...activityLikesAndComments
          }
        }
        itemType
      }
    }
    ${studentFragment.basicStudentDetails}
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${likefragment.like}
    ${postSeenFragment.postSeen}
    ${taggedPracticesFragment.taggedPracticesDetails}
    ${folderFragments.folderItem}
    ${toolResponseFragment.toolResponse}
    ${postResolvedFieldValueFragment.resolvedMinimalTree}
    ${postPypElementRatingFragment.studentElementRating}
    ${messageCommentOfPostFragment.message}
    ${activityLikeCommentFragment.likesAndComments}
    ${resourceFragments.assessmentItem}
  `,
  journalPostDetail: gql`
    fragment postItem on Post {
      id
      title
      ibPYPElementRatings {
        ...postRatingsItem
      }
      titleTranslationItem(filters: $translationFilter) {
        label
        showTranslate
        sourceLanguage
        translatedLabel
      }
      attachments {
        ...attachmentItem
        displaySequence
      }
      createdBy {
        ...userItem
      }
      comments {
        totalCount
        unreadCount
        edges: messageEdges {
          node {
            ...messageItem
          }
        }
      }
      likes {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
      seen {
        totalCount
        edges {
          node {
            ...postSeenItem
          }
        }
      }
      createdAt
      publishedAt
      students {
        totalCount
        edges {
          node {
            ...studentItem
            alertTypes {
              ...alertTypeFields
            }
          }
        }
      }
      unitPlan {
        allFields {
          ...unitPlanAllFields
        }
        ...unitPlanItem
      }
      resourceType
      resourceItem {
        id
        title {
          id
          value
        }
        image {
          id
          value
        }
        templateId
        ... on Assessment {
          assessmentType {
            id
            value
          }
          taskType {
            id
            type
            label
          }
        }
      }
      elementPYP {
        id
        fieldUID
        value
        parentType
        resolvedMinimalTree {
          ...resolvedMinimalTreeItem
        }
      }
      folders {
        ...folderItem
      }
      taggedPractices {
        ...taggedPracticesDetails
      }
      state
      aiConversation {
        edges {
          node {
            id
          }
        }
      }
      course {
        id
        title
        learningCourse {
          id
        }
      }
      academicTerm {
        id
        label
        startDate
        endDate
      }
      postTaggedEntity {
        id
        entityType
        entity {
          ... on UnitPlan {
            allFields {
              ...unitPlanAllFields
            }
            ...unitPlanItem
          }
          ... on Assessment {
            ...assessmentItem
            unitPlan {
              ...unitPlanItem
            }
          }
        }
      }
      isPrivate
      isDeleted
      isHiddenFromStudents
      rejectComment
      isStudentIndividuallyRated
      items {
        item {
          id
          ... on StudentAssignment {
            id
            isEvaluated
            isSharedWithStudent
            student {
              ...studentItem
            }
            submission {
              id
              response
              attachmentGroups {
                id
                attachments {
                  ...attachmentItem
                }
              }
            }
            toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
              id
              toolId
              toolType
              toolResponses {
                ...toolResponseItem
              }
            }
            ibPYPElementRatings {
              ...ratingItem
            }
            elementRemarks(filters: { creatorTypes: [STAFF] }) {
              id
              message
              ibPYPElementId
              ibPYPElementType
            }
            assignment {
              id
              isStudentEvaluationEnabled
              isTeacherEvaluationEnabled
              content {
                ... on Assessment {
                  id
                  title {
                    id
                    value
                  }
                  assessmentTools {
                    edges {
                      node {
                        id
                        assessmentToolItem {
                          id
                          ... on MypObjectiveRubric {
                            id
                            label
                          }
                          ... on ScoreAssessmentTool {
                            id
                            scoreToolData {
                              id
                              maxScore
                            }
                          }
                        }
                        assessmentToolType
                        isTeacherEvaluationEnable
                        isStudentEvaluationEnable
                      }
                    }
                  }
                  assessmentToolType
                  allFields {
                    id
                    uid
                    value
                    resolvedMinimalTree {
                      ...resolvedMinimalTreeItem
                    }
                  }
                  template {
                    ...templateItem
                  }
                  isLearningGoalsEvaluationEnabled
                }
              }
            }
          }
          ... on StudentActivity {
            id
            metadata
            startTime
            endTime
            description
            individuallyTracked
            activityType
            attachmentGroups {
              id
              attachments {
                ...attachmentItem
              }
              displaySequence
              metadata
            }
            activityStudent: student {
              ...studentItem
            }
            ibPYPElementRatings {
              id
              academicCriteriaSet {
                id
                label
                displaySequence
              }
              academicCriteriaValue {
                id
                label
                metadata
              }
              metadata
            }
            ...activityLikesAndComments
          }
        }
        itemType
      }
      curriculumProgram {
        id
        type
      }
      course {
        id
        grade {
          id
        }
        primaryGrade {
          id
        }
      }
    }
    ${studentsAlertFragment}
    ${postResolvedFieldValueFragment.resolvedMinimalTree}
    ${studentFragment.basicStudentDetails}
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${unitPlanFragment.unitPlanDetails}
    ${folderFragments.folderItem}
    ${likefragment.like}
    ${postSeenFragment.postSeen}
    ${taggedPracticesFragment.taggedPracticesDetails}
    ${toolResponseFragment.toolResponse}
    ${plannerFieldFragment.unitPlanAllFields}
    ${plannerTemplateFragment.plannerTemplate}
    ${postPypElementRatingFragment.studentElementRating}
    ${messageCommentOfPostFragment.message}
    ${activityLikeCommentFragment.likesAndComments}
    ${postRatingsFragment.postRatingsItem}
    ${resourceFragments.assessmentBasicDetailsV2}
  `,
  translationPostItem: gql`
    fragment translationPostItem on Post {
      id
      title
      translatedText: titleTranslationItem(filters: $translationFilter) {
        label
        translatedLabel
      }
    }
  `,
  postLikesDetails: gql`
    fragment postItem on Post {
      id
      students {
        edges {
          node {
            ...userItem
          }
        }
      }
      likes {
        totalCount
        edges {
          node {
            id
            user {
              id
              type
              ...userItem
              ... on Student {
                alertTypes {
                  ...alertTypeFields
                }
              }
              ... on FamilyMember {
                children {
                  ...userItem
                }
              }
            }
          }
        }
      }
    }
    ${studentsAlertFragment}
    ${userFragment.basicUserDetails}
  `,
  postSeenDetails: gql`
    fragment postItem on Post {
      id
      students {
        edges {
          node {
            ...userItem
          }
        }
      }
      seen {
        totalCount
        edges {
          node {
            id
            user {
              id
              type
              ...userItem
              ... on FamilyMember {
                children {
                  ...userItem
                }
              }
            }
          }
        }
      }
    }
    ${userFragment.basicUserDetails}
  `,
  postSelection: gql`
    fragment postSelectionItem on Post {
      id
      title
      attachments {
        ...attachmentItem
      }
      createdBy {
        ...userItem
      }
      items {
        item {
          id
          ... on StudentAssignment {
            id
            assignment {
              id
              content {
                id
                ... on Assessment {
                  id
                  assessmentType {
                    id
                    value
                  }
                  isLearningGoalsEvaluationEnabled
                }
              }
            }
            submission {
              id
              attachmentGroups {
                id
                attachments {
                  ...attachmentItem
                }
              }
            }
          }
        }
        itemType
      }
    }
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
  `,
};

export const parentPostFragment = {
  parentPost: gql`
    fragment parentItem on FamilyMember {
      id
      posts(last: 5, after: $after, filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            ...postItem
          }
        }
      }
    }
    ${journalPostFragment.journalPost}
  `,
};

export const coursePostFragment = {
  coursePost: gql`
    fragment courseItem on Course {
      id
      posts(last: 5, after: $after, filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            ...postItem
          }
        }
      }
    }
    ${journalPostFragment.journalPostFeed}
  `,
};

export const courseStudentFragment = {
  courseStudent: gql`
    fragment courseItem on Course {
      id
      postCount(
        folderIds: $folderIds
        isShowHiddenFromStudents: $isShowHiddenFromStudents
        filters: $filters
      ) {
        draft
        published
        inApproval
        scheduled
        teacherInApproval
        studentInApproval
        unTagged {
          draft
          published
          inApproval
          scheduled
          teacherInApproval
          studentInApproval
        }
      }
      students: studentPostCount(
        folderIds: $folderIds
        includeRemovedStudentIds: $includeRemovedStudentIds
        archivalState: ALL
        isShowHiddenFromStudents: $isShowHiddenFromStudents
        filters: $filters
      ) {
        postCount
        draft
        published
        inApproval
        teacherInApproval
        studentInApproval
        scheduled
        node: student {
          ...studentItem
          alertTypes {
            ...alertTypeFields
          }
        }
      }
    }
    ${studentsAlertFragment}
    ${studentCourseDetailFragment.basicStudentDetails}
  `,
  courseFiltersStudent: gql`
    fragment courseItem on Course {
      id
      students(
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        edges {
          node {
            ...studentItem
            alertTypes {
              ...alertTypeFields
            }
          }
        }
      }
    }
    ${studentsAlertFragment}
    ${studentCourseDetailFragment.basicStudentDetails}
  `,
};

export const studentPostFragment = {
  studentPost: gql`
    fragment studentPostItem on Student {
      id
      posts(last: 5, after: $after, filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...postItem
          }
        }
      }
    }
    ${journalPostFragment.journalPostFeed}
  `,
  studentPostCount: gql`
    fragment studentPostItem on Student {
      id
      posts(last: 5, after: $after, filters: $filters) {
        totalCount
      }
    }
  `,
  studentProfilePortfolio: gql`
    fragment studentPostItem on Student {
      id
      posts(last: 9, after: $after, filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...postItem
          }
        }
      }
    }
    ${journalPostFragment.journalPostFeedStudentProfile}
  `,
};

export const nodePostSelectionFragment = {
  organizationPost: gql`
    fragment organizationPostItem on Organization {
      id
      posts(last: 20, after: $after, filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...postSelectionItem
          }
        }
      }
    }
    ${journalPostFragment.postSelection}
  `,
  studentPost: gql`
    fragment studentPostItem on Student {
      id
      posts(last: 21, after: $after, filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...postSelectionItem
          }
        }
      }
    }
    ${journalPostFragment.postSelection}
  `,
};

export const PostCommentsWithUnreadEdgesFragment = {
  post: gql`
    fragment postItem on Post {
      id
      comments {
        ... on CommentConnection {
          totalCount
          unreadCount
          edges: messageEdges {
            node {
              ...messageItem
            }
          }
          unreadCommentsEdges: unreadMessageEdges {
            node {
              ...messageItem
            }
          }
        }
      }
    }
    ${messageCommentOfPostFragment.message}
  `,
  postWithVariables: gql`
    fragment postItem on Post {
      id
      comments {
        ... on CommentConnection {
          totalCount
          unreadCount
          edges: messageEdges(first: $first, after: $after) {
            node {
              ...messageItem
            }
          }
          unreadCommentsEdges: unreadMessageEdges {
            node {
              ...messageItem
            }
          }
        }
      }
    }
    ${messageCommentOfPostFragment.message}
  `,
};

export const PostCommentFragment = {
  post: gql`
    fragment postItem on Post {
      id
      comments {
        ... on CommentConnection {
          totalCount
          unreadCount
          edges: messageEdges {
            node {
              ...messageItem
            }
          }
        }
      }
    }
    ${messageCommentOfPostFragment.message}
  `,
  postWithVariables: gql`
    fragment postItem on Post {
      id
      comments {
        ... on CommentConnection {
          totalCount
          unreadCount
          edges: messageEdges(first: $first, after: $after) {
            node {
              ...messageItem
            }
          }
        }
      }
    }
    ${messageCommentOfPostFragment.message}
  `,
};

export const PostTermFragment = {
  academicYearBasicDetailsItem: gql`
    fragment academicYearBasicDetailsItem on AcademicYear {
      id
      startDate
      endDate
      isCurrentAcademicYear
      isUpcomingAcademicYear
      isPreviousAcademicYear
      terms(filters: $termsFilters) {
        id
        label
        type
        startDate
        endDate
        lockingStatus
        curriculumProgram {
          id
          __typename
        }
        __typename
      }
      __typename
    }
  `,
};

export const postWithGoalsFragment = {
  postWithGoals: gql`
    fragment postItem on Post {
      id
      title
      state
      description
      students {
        totalCount
      }
      attachments {
        ...attachmentItem
      }
      elementPYP {
        id
        fieldUID
        value
        parentType
        resolvedMinimalTree {
          ...resolvedMinimalTreeItem
        }
      }
      unitPlan {
        ...unitPlanItem
      }
      resourceItem {
        ... on Assessment {
          ...assessmentItem
        }
        ... on LearningEngagement {
          ...leItem
        }
      }
      postTaggedEntity {
        id
        entityType
        entity {
          ... on UnitPlan {
            ...unitPlanItem
          }
          ... on Assessment {
            ...assessmentItem
            unitPlan {
              ...unitPlanItem
            }
          }
        }
      }
      resourceType
      updatedAt
      publishedAt
      createdAt
    }
    ${attachmentFragment.attachment}
    ${unitPlanFragment.unitPlan}
    ${resourceFragments.assessmentItem}
    ${resourceFragments.leItem}
    ${postResolvedFieldValueFragment.resolvedMinimalTree}
  `,
};

export const studentCourseFragment = gql`
  fragment studentCoursesItem on Student {
    id
    firstName
    lastName
    pronouns
    preferredName
    prefix
    suffix

    courseMap(filters: $filters) {
      isUserRemovedFromCourse
      course {
        id
        title
        enabledModules(filters: $enabledModulesFilters)
        aclControlParameters(filters: $aclControlParameterFilter) {
          id
          uid
          permission
          value
        }
        learningCourse {
          id
          title
          displayName
          academicCourse {
            id
            label
          }
        }
        profileImage
        isArchived
        isBookmark
        folders {
          id
          name
          color
          totalPostCount(filters: $postFilters)
        }
        curriculumProgram {
          id
        }
        profileImageData {
          url
          icon
          __typename
        }
        __typename
      }

      __typename
    }
    __typename
  }
`;

export const staffDetailsFragment = gql`
  fragment staffItem on Staff {
    id
    entityRelationMap(filters: $entityRelationMapFilters) {
      edges {
        node {
          relatedEntity {
            ... on Staff {
              id
              firstName
              lastName
              middleName
              email
              isArchived
              prefix
              suffix
              pronouns
              preferredName
            }
          }
          relationType
          entityType
          relatedEntityType
        }
        cursor
      }
    }
  }
`;

export const plannerFragment = {
  plannerElementNodeFragment: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      uid
      type
      label
      subText
      gradingType
      code
      depth
      parentData {
        id
        uid
      }
      standardsWrapper {
        standardSet {
          id
          type
          title
        }
        properties {
          isArchived
          isPowerStandard
          isGradedStandard
          subjects {
            id
            name
          }
          grades {
            id
            name
          }
          tags {
            id
            label
          }
        }
      }

      standardCountWrapper {
        assignment {
          totalCount
        }
        assessment {
          totalCount
        }
        course {
          totalCount
        }
        portfolio {
          totalCount
        }
        unitPlan {
          totalCount
        }
      }
    }
  `,
  plannerBenchmark: gql`
    fragment plannerBenchmarkItem on PlannerBenchmark {
      id
      uid
      label
      type
      subText
      gradingType
      code
      depth
      parent
      parentData {
        id
        uid
      }
      benchmarkQuestionCount {
        count
      }
      standardsWrapper {
        standardSet {
          id
          title
        }
        properties {
          isArchived
          isPowerStandard
          isGradedStandard
          grades {
            id
            name
          }
          tags {
            id
            label
          }
        }
      }

      standardCountWrapper {
        assignment {
          totalCount
        }
        assessment {
          totalCount
        }
        course {
          totalCount
        }
        portfolio {
          totalCount
        }
        unitPlan {
          totalCount
        }
      }
    }
  `,
};

export const plannerElementNodeFragment = {
  basicPlannerElementNodeDetail: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      label
      subText
    }
  `,
  plannerElementNode: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      displaySequence
      label
      type
      subText
      depth
      children
      parent
      subType
      isLeaf
      code
      metadata
      updatedAt
      gradingType
      plannerElementNodeWrapper {
        properties {
          isGradedStandard
        }
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
      standardsWrapper {
        standardSet {
          id
          title
        }
        properties {
          isGradedStandard
          tags {
            id
            label
          }
        }
      }
      associatedParents {
        id
        ... on Grade {
          id
          name
        }
        ... on Subject {
          id
          name
          isArchived
        }
        ... on SubjectGroup {
          ...subjectGroupBasicDetailsItem
        }
        ... on PlannerElementNode {
          id
          label
          type
          metadata
        }
      }
      tags: genericTags {
        id
        label
        icon {
          id
          url
        }
      }
    }
    ${subjectGroupFragment.subjectGroupBasicDetails}
  `,
  plannerElementNodeWithChild: gql`
    fragment plannerElementNodeWithChildItem on PlannerElementNode {
      id
      label
      type
      associatedParents {
        id
        ... on Course {
          id
          title
        }
      }
      associatedChildPlannerElementNodes {
        id
        label
        type
        fields {
          id
          uid
          value
        }
      }
    }
  `,
  plannerElementDepthLabels: gql`
    fragment plannerElementDepthLabelItem on PlannerElementDepthLabel {
      id
      depth
      label
      totalNodes
    }
  `,
  plannerElementNodeV2: gql`
    fragment plannerElementNodeV2Item on PlannerElementNode {
      id
      label
      type
      depth
      children
      parent
      subType
      isLeaf
      code
      gradingType
      plannerElementNodeWrapper {
        properties {
          isGradedStandard
        }
      }
      dataSetNodeWrapper {
        properties {
          isGraded
          isGradedStandard
          subjects {
            id
            name
            isArchived
          }
          grades {
            id
            name
          }
          subjectGroups {
            id
            name
          }
          associatedPlannerElementNodes {
            id
            label
            type
            metadata
          }
          tags {
            id
            label
          }
        }
      }
    }
  `,
};

export const plannerElementNodeSetFragment = {
  plannerElementNodeSet: gql`
    fragment plannerElementNodeSetItem on PlannerElementNodeSetConnection {
      edges {
        node {
          id
          type
          title
          plannerElementSet {
            id
            type
          }
        }
      }
    }
  `,
  plannerElementNodeSetWithNodes: gql`
    fragment plannerElementNodeSetWithNodesItem on PlannerElementNodeSet {
      id
      title
      type
      plannerElementSet {
        id
        type
        label
        config {
          id
          config
          valueType
          value
        }
      }
      plannerElementNodeSetWrapperV2 {
        ... on PlannerElementNodeSetWrapper {
          publishedData(filters: $plannerElementNonAtlFilters) {
            edges {
              node {
                ...plannerElementNodeV2Item
              }
            }
          }
        }
        ... on PlannerActionSetWrapper {
          publishedData(filters: $plannerElementNonAtlFilters) {
            edges {
              node {
                ...actionItem
              }
            }
          }
        }
        ... on PlannerLearnerProfileSetWrapper {
          publishedData(filters: $plannerElementNonAtlFilters) {
            edges {
              node {
                ...lpItem
              }
            }
          }
        }
        ... on PlannerConceptSetWrapper {
          publishedData(filters: $plannerElementNonAtlFilters) {
            edges {
              node {
                ...conceptItem
              }
            }
          }
        }
        ... on PlannerRelatedConceptSetWrapper {
          publishedData(filters: $plannerElementNonAtlFilters) {
            edges {
              node {
                ...relatedConceptItem
              }
            }
          }
        }
        ... on PlannerATLSetWrapper {
          publishedData(filters: $plannerElementATLFilters) {
            edges {
              node {
                ...atlItem
              }
            }
          }
        }
      }
    }
    ${plannerElementNodeFragment.plannerElementNodeV2}
    ${pypElementFragment.action}
    ${pypElementFragment.lp}
    ${pypElementFragment.concept}
    ${pypElementFragment.relatedConcept}
    ${pypElementFragment.atl}
  `,
};

export const plannerElementSetFragment = {
  plannerElementSetWithNodes: gql`
    fragment plannerElementSetWithNodesItem on PlannerElementSet {
      id
      label
      type
      nodes(filters: $filters) {
        ...plannerElementNodeItem
      }
    }
    ${plannerElementNodeFragment.plannerElementNode}
  `,
  plannerElementSetWithNodesAndChild: gql`
    fragment plannerElementSetWithNodesAndChildItem on PlannerElementSet {
      id
      label
      type
      nodes(filters: $filters) {
        ...plannerElementNodeWithChildItem
      }
    }
    ${plannerElementNodeFragment.plannerElementNodeWithChild}
  `,
  plannerElementSetBasicDetails: gql`
    fragment plannerElementSetBasicDetailsItem on PlannerElementSet {
      id
      label
      type
    }
  `,
  plannerElementAssociatedChildPlannerElementNodes: gql`
    fragment plannerElementAssociatedChildPlannerElementNodesItem on PlannerElementNode {
      id
      label
      type
    }
  `,
};

export const attachmentFragmentForStudentAssignment = {
  attachment: gql`
    fragment attachmentItem on Attachment {
      id
      name
      type
      mimeType
      url
      signedUrl
      thumbUrl
      title
      metadata
      streamUrl
      parentType
      isRead
      similarityReport {
        ...similarityItem
      }
      childAttachments {
        id
        type
      }
      createdBy {
        id
        type
      }
      lockingState {
        state
        scheduledAt
      }
      questionAttachment {
        ... on QuestionSubmission {
          id
          questionScore
          score {
            id
            value
          }
        }
        ... on Question {
          id
          questionScore
        }
      }
    }
    ${similarityFragment.similarityItem}
  `,
};

export const attachmentGroupsFragmentForStudentAssignment = {
  attachmentGroup: gql`
    fragment attachmentGroupItem on AttachmentGroup {
      id
      metadata
      attachments {
        ...attachmentItem
      }
      studentTemplate {
        id
      }
    }
    ${attachmentFragmentForStudentAssignment.attachment}
  `,
};

export const attachmentGroupsNodeFragmentForStudentAssignment = {
  studentAssignmentSubmissionAttachmentGroups: gql`
    fragment studentAssignmentSubmissionAttachmentGroupsItem on StudentAssignmentSubmission {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ${attachmentGroupsFragmentForStudentAssignment.attachmentGroup}
  `,
};

export const subjectStandardFragment = gql`
  fragment subjectStandardFragment on PlannerElementNodeSetConnection {
    edges {
      node {
        id
        title
        isArchived
        subject {
          id
          name
          isArchived
        }
        standardSetWrapper {
          publishedStandards {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const subjectStandardFragmentPYP = gql`
  fragment subjectStandardFragmentPYP on PlannerElementNodeSetConnection {
    edges {
      node {
        id
        title
        subject {
          id
          name
          isArchived
        }
        isArchived
        standardSetWrapper {
          publishedStandards: publishedBenchmarks {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const standardsFragment = gql`
  fragment standardsFragment on PlannerElementNode {
    id
    uid
    label
    subText
    gradingType
    code
    depth
    parentData {
      id
    }
    standardsWrapper {
      standardSet {
        id
        title
      }
      properties {
        isArchived
        isPowerStandard
        weight
        subjects {
          id
          name
        }
        grades {
          id
          name
        }
        tags {
          id
          label
        }
      }
    }
  }
`;

export const benchmarksFragment = gql`
  fragment benchmarksFragment on PlannerBenchmark {
    id
    uid
    label
    subText
    gradingType
    code
    depth
    levelId
    parentData {
      id
    }
    standardsWrapper {
      standardSet {
        id
        title
      }
      properties {
        isArchived
        isPowerStandard
        weight
        grades {
          id
          name
        }
        tags {
          id
          label
        }
      }
    }
  }
`;
